import { useEffect, useState } from "react";

import { readBrandTokens, type BrandTokens } from "@/lib/brandTokens";

/**
 * Reactive view of the brand colour tokens defined in `src/index.css`.
 *
 * CSS-driven consumers pick up an override of `--color-coral` (etc.) for free.
 * JS-driven consumers — chiefly Ant Design's `ConfigProvider`, whose theme
 * tokens are read at render time — need to be told the value changed, so this
 * hook watches the two ways an override can land:
 *
 *   1. inline custom properties set on `<html>` (`style` attribute), and
 *   2. a stylesheet injected into `<head>`.
 *
 * Both are re-read through `getComputedStyle`, so the hook never needs to know
 * how the override was applied — only that something changed.
 */
export function useBrandTokens(): BrandTokens {
	const [tokens, setTokens] = useState<BrandTokens>(() => readBrandTokens());

	useEffect(() => {
		if (typeof MutationObserver === "undefined") return;

		const resync = () => {
			const next = readBrandTokens();
			setTokens((current) => {
				const changed = (Object.keys(next) as (keyof BrandTokens)[]).some(
					(key) => current[key] !== next[key],
				);
				return changed ? next : current;
			});
		};

		// The first paint may land before the stylesheet resolves, so re-read once
		// on mount rather than trusting the lazy initial state alone.
		resync();

		const rootObserver = new MutationObserver(resync);
		rootObserver.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ["style", "class", "data-theme"],
		});

		const headObserver = new MutationObserver(resync);
		headObserver.observe(document.head, { childList: true });

		return () => {
			rootObserver.disconnect();
			headObserver.disconnect();
		};
	}, []);

	return tokens;
}
